import {RdvBlockedSlotPayloadSchema, type RdvBlockedSlotResponse} from "@mytour/shared";
import {FloatingField} from "@mytour/ui/form";
import {Splitter} from "@mytour/ui/layout";
import {type Control, Controller, type FieldErrors} from "react-hook-form";
import {FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

export const blockedSlotFormSchema = z
  .object({
    date: z.string(),
    allDay: z.boolean(),
    startTime: z.string(),
    endTime: z.string(),
  })
  .superRefine((value, context) => {
    const result = RdvBlockedSlotPayloadSchema.safeParse(
      value.allDay ? {date: value.date, allDay: true} : value
    );
    if (!result.success) {
      for (const issue of result.error.issues) {
        context.addIssue({
          ...issue,
          message:
            issue.path[0] === "date"
              ? "Choisissez une date valide."
              : issue.code === "invalid_string"
                ? "Saisissez une heure valide."
                : issue.message,
        });
      }
    }
  });
export type BlockedSlotFormData = z.infer<typeof blockedSlotFormSchema>;

export function getBlockedSlotDefaultValues(slot?: RdvBlockedSlotResponse): BlockedSlotFormData {
  return {
    date: slot?.date ?? "",
    allDay: slot?.allDay ?? false,
    startTime: slot && !slot.allDay ? slot.startTime : "",
    endTime: slot && !slot.allDay ? slot.endTime : "",
  };
}

type Props = {
  control: Control<BlockedSlotFormData>;
  errors: FieldErrors<BlockedSlotFormData>;
  disabled: boolean;
  allDay: boolean;
};

export function BlockedSlotForm({control, errors, disabled, allDay}: Props) {
  return (
    <>
      <FloatingField
        name="date"
        label="Date"
        type="date"
        control={control}
        error={errors.date}
        disabled={disabled}
      />
      <FormGroup switch className="mb-3">
        <Controller
          name="allDay"
          control={control}
          render={({field}) => (
            <Input
              type="checkbox"
              role="switch"
              id="blocked-slot-all-day"
              checked={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              innerRef={field.ref}
              disabled={disabled}
            />
          )}
        />
        <Label check for="blocked-slot-all-day">
          Journée entière
        </Label>
      </FormGroup>
      <Splitter>
        <FloatingField
          name="startTime"
          label="Heure de début"
          type="time"
          control={control}
          error={allDay ? undefined : errors.startTime}
          disabled={disabled || allDay}
        />
        <FloatingField
          name="endTime"
          label="Heure de fin"
          type="time"
          control={control}
          error={allDay ? undefined : errors.endTime}
          disabled={disabled || allDay}
        />
      </Splitter>
      <p className="small text-muted" aria-live="polite">
        {allDay
          ? "Toute la date est bloquée, quels que soient vos horaires d'activité. Les heures grisées ne sont pas prises en compte."
          : "Choisissez les heures de début et de fin."}
      </p>
    </>
  );
}
